import { useState, useMemo, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { ChevronDown, ChevronRight, Plus, Loader2, EyeOff, Check, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { Invoice, InvoiceGroupDetails } from '../../../redux/billing/billing.types';
import { Company } from 'src/pages/billing/types/billing.types';
import { useUpdateInvoiceMutation, useUpdateInvoiceLineItemMutation, useGetInvoiceGroupDetailsQuery, useCreateInvoiceMutation, useCreateInvoiceLineItemMutation, useUpdateInvoiceStatusMutation, useSyncInvoiceMutation } from '../../../redux/billing/billing.endpoints';
import newDashEmptyGraphic from "../../../assets/images/new-dash-empty.png";
import { useGetPlatformsQuery } from "../../../redux/admin/reporting/reporting.endpoints";
import { useGetTermsQuery, useGetSKUsQuery } from "../../../redux/billing/billing.endpoints";
import { Input } from 'src/components/ui/input';
import { InvoiceLineItemOptionsDropdown, InvoiceOptionsDropdown } from './invoice-table-options';

interface InvoiceTableProps {
  searchValue: string;
  viewHidden: boolean;
  selectedGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails;
  isLoadingDetails: boolean;
  companies?: Company[];
  isGeneratingInvoices?: boolean;
  onGenerationComplete?: () => void;
  onOpenPreviewModal?: (groupId: string) => void;
  onGenerateInvoices?: (groupId: string) => void;
}

export const InvoiceTable = ({searchValue, viewHidden, selectedGroupId, invoiceGroupDetails, isLoadingDetails, companies = [], isGeneratingInvoices = false, onGenerationComplete, onOpenPreviewModal, onGenerateInvoices}: InvoiceTableProps) => {
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [expandedLineItems, setExpandedLineItems] = useState<Set<string>>(new Set());
  // const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [newLineItemInputs, setNewLineItemInputs] = useState<Record<string, string>>({});
  const [showNewLineItemInputs, setShowNewLineItemInputs] = useState<Set<string>>(new Set());

  // Local & optimistic states for patches & generation
  const [textFieldValues, setTextFieldValues] = useState<Record<string, string>>({});
  const [optimisticInvoiceGroupDetails, setOptimisticInvoiceGroupDetails] = useState<InvoiceGroupDetails | undefined>(invoiceGroupDetails);
  const [isGenerating, setIsGenerating] = useState(false);

  const [useSyncInvoice] = useSyncInvoiceMutation();
  const [updateInvoiceStatus] = useUpdateInvoiceStatusMutation();
  const [updateInvoice] = useUpdateInvoiceMutation();
  const [updateInvoiceLineItem] = useUpdateInvoiceLineItemMutation();
  const [createInvoice] = useCreateInvoiceMutation();
  const [createInvoiceLineItem] = useCreateInvoiceLineItemMutation();
  const { refetch: refetchInvoiceGroupDetails } = useGetInvoiceGroupDetailsQuery(selectedGroupId, {
    skip: !selectedGroupId,
  });

  const { data: platforms = [] } = useGetPlatformsQuery({});
  const { data: terms = [] } = useGetTermsQuery();
  const { data: skus = [] } = useGetSKUsQuery();

  // const handleCreateNewInvoice = async(companyId: string) => {
  //   try {
  //     await createInvoice({
  //       invoice_group: selectedGroupId,
  //       company: companyId,
  //       attention_needed: true,
  //     }).unwrap();
  //     if (refetchInvoiceGroupDetails) {
  //       await refetchInvoiceGroupDetails();
  //     }
  //   } catch (error) {
  //     console.error('Failed to create invoice:', error);
  //   }
  // };

  const handleCreateNewLineItem = async(invoiceId: string, name: string) => {
    try {
      await createInvoiceLineItem({
        name,
        invoice_group: selectedGroupId,
        invoice: invoiceId,
        attention_needed: true,
      }).unwrap();

      setNewLineItemInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[invoiceId];
        return newInputs;
      });
      setShowNewLineItemInputs(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });

      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to create line item:', error);
    }
  };

  const handleNewLineItemInputChange = (invoiceId: string, value: string) => {
    setNewLineItemInputs(prev => ({
      ...prev,
      [invoiceId]: value,
    }));
  };

  const handleNewLineItemKeyDown = (invoiceId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const name = newLineItemInputs[invoiceId]?.trim();
      if (name) {
        handleCreateNewLineItem(invoiceId, name);
      }
    }
  };

  const toggleNewLineItemInput = (invoiceId: string) => {
    setShowNewLineItemInputs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
        setNewLineItemInputs(prevInputs => {
          const newInputs = { ...prevInputs };
          delete newInputs[invoiceId];
          return newInputs;
        });
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
    if (invoiceGroupDetails) {
      setTextFieldValues({});

      const hasInvoices = invoiceGroupDetails.invoices && invoiceGroupDetails.invoices.length > 0;

      // Only show generating state if invoices were actually requested AND no invoices exist yet
      if (isGeneratingInvoices && !hasInvoices) {
        setIsGenerating(true);
      } else {
        setIsGenerating(false);
        // If we have invoices now, notify parent that generation is complete
        if (hasInvoices && isGeneratingInvoices && onGenerationComplete) {
          onGenerationComplete();
        }
      }
    }
  }, [invoiceGroupDetails, isGeneratingInvoices, onGenerationComplete]);

  // Poll every 30 seconds during invoice group generation
  useEffect(() => {
    if (!isGenerating || !selectedGroupId) return;

    const interval = setInterval(async() => {
      try {
        if (refetchInvoiceGroupDetails) {
          const result = await refetchInvoiceGroupDetails();
          const updatedDetails = result.data;
          if (updatedDetails && updatedDetails.invoices && updatedDetails.invoices.length > 0) {
            setIsGenerating(false);
          }
        }
      } catch (error) {
        console.error('Failed to refetch invoice group details:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isGenerating, selectedGroupId, refetchInvoiceGroupDetails]);

  const companyLookup = useMemo(() => {
    if (!companies) return {};
    return companies.reduce((acc, company) => {
      acc[company.id] = company;
      return acc;
    }, {} as Record<string, Company>);
  }, [companies]);

  const termsLookup = useMemo(() => {
    if (!terms) return {};
    return terms.reduce((acc, term) => {
      acc[term.id] = term;
      return acc;
    }, {} as Record<string, any>);
  }, [terms]);

  const skusLookup = useMemo(() => {
    if (!skus) return {};
    return skus.reduce((acc, sku) => {
      acc[sku.id] = sku;
      return acc;
    }, {} as Record<string, any>);
  }, [skus]);

  const getCompanyName = (companyId: string): string => {
    // Handle unassigned orphan items (no company)
    if (!companyId || !companyLookup[companyId]) {
      const unassignedLineItemCount = (optimisticInvoiceGroupDetails?.invoices || [])
        .filter(invoice => !invoice.company || !companyLookup[invoice.company])
        .reduce((total, invoice) => total + invoice.invoice_line_items.length, 0);
      return `🚨 [${unassignedLineItemCount} UNASSIGNED ${unassignedLineItemCount === 1 ? 'ITEM' : 'ITEMS'}!]`;
    }

    const company = companyLookup[companyId];
    return company?.company_name || companyId;
  };

  const getInvoiceChannelDisplay = (invoice: Invoice): string => {
    if (!invoice.invoice_line_items || invoice.invoice_line_items.length === 0) {
      return '-';
    }

    const channels = [...new Set(invoice.invoice_line_items.map(item => item.channel).filter(Boolean))];

    if (channels.length === 0) {
      return '-';
    }

    if (channels.length === 1) {
      return channels[0].charAt(0).toUpperCase() + channels[0].slice(1);
    }

    return 'Multiple';
  };

  const getInvoiceTotals = (invoice: Invoice) => {
    return invoice.invoice_line_items.reduce(
      (totals, item) => ({
        impressions: totals.impressions + item.qty,
        adSpend: totals.adSpend + item.advertiser_spend,
        amountToBill: totals.amountToBill + (item.do_not_bill ? 0 : item.amount_to_bill || 0),
      }),
      { impressions: 0, adSpend: 0, amountToBill: 0 }
    );
  };

  const formatCurrency = (amount: number | null): string => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPercentage = (percent: number): string => {
    return `${(percent * 100).toFixed(1)}%`;
  };

  const formatLastSynced = (updatedAt: string | null): string => {
    if (!updatedAt) {
      return 'Not yet synced';
    }

    const now = new Date();
    const updated = new Date(updatedAt);
    const diffInSeconds = Math.floor((now.getTime() - updated.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Synced now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `Synced ${diffInMinutes}m ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `Synced ${diffInHours} hr${diffInHours !== 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `Synced ${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  const getTermNameById = (termId: string): string => {
    const term = termsLookup[termId];
    return term ? term.name : termId;
  };

  const getSkuNameById = (skuId: string): string => {
    const sku = skusLookup[skuId];
    return sku ? sku.name : skuId;
  };

  const sortedCompanyNames = useMemo(() => {
    if (!companies) return [];
    return companies
      .map(company => ({
        id: company.id,
        name: company.company_name || company.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companies]);

  const sortedTerms = useMemo(() => {
    if (!terms) return [];
    return terms
      .map(term => ({
        id: term.id,
        name: term.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [terms]);

  const sortedSKUs = useMemo(() => {
    if (!skus) return [];
    return skus
      .map(sku => ({
        id: sku.id,
        name: sku.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [skus]);

  // Sort invoices by name and line items within each invoice by ID for consistent display
  const sortedInvoices = useMemo(() => {
    const invoices = optimisticInvoiceGroupDetails?.invoices || [];
    return invoices
      .slice()
      .sort((a, b) => {
        // Orphans first
        const aHasCompany = a.company && companyLookup[a.company];
        const bHasCompany = b.company && companyLookup[b.company];

        if (!aHasCompany && bHasCompany) return -1;
        if (aHasCompany && !bHasCompany) return 1;

        // Else sort by company name
        const aCompanyName = getCompanyName(a.company);
        const bCompanyName = getCompanyName(b.company);
        return aCompanyName.localeCompare(bCompanyName);
      })
      .map(invoice => ({
        ...invoice,
        invoice_line_items: invoice.invoice_line_items.slice().sort((a, b) => a.id.localeCompare(b.id)),
      }));
  }, [optimisticInvoiceGroupDetails, companyLookup, getCompanyName]);

  const filteredInvoices = useMemo(() => {
    if (!searchValue.trim()) {
      return sortedInvoices;
    }

    const searchLower = searchValue.toLowerCase();

    return sortedInvoices.filter(invoice => {
      const invoiceMatches =
        (invoice.id || '').toLowerCase().includes(searchLower) ||
        getCompanyName(invoice.company || '').toLowerCase().includes(searchLower) ||
        getInvoiceChannelDisplay(invoice).toLowerCase().includes(searchLower) ||
        formatNumber(getInvoiceTotals(invoice).impressions).includes(searchLower) ||
        formatCurrency(getInvoiceTotals(invoice).adSpend).toLowerCase().includes(searchLower) ||
        formatCurrency(getInvoiceTotals(invoice).amountToBill).toLowerCase().includes(searchLower) ||
        (invoice.invoice_status || '').toLowerCase().includes(searchLower) ||
        (companyLookup[invoice.company]?.emails || '').toLowerCase().includes(searchLower) ||
        (invoice.terms && getTermNameById(invoice.terms).toLowerCase().includes(searchLower)) ||
        (invoice.has_balance && 'has balance'.includes(searchLower)) ||
        (!invoice.has_balance && 'no balance'.includes(searchLower));

      const lineItemMatches = invoice.invoice_line_items.some(lineItem => {
        return (
          (lineItem.name || '').toLowerCase().includes(searchLower) ||
          (lineItem.id || '').toLowerCase().includes(searchLower) ||
          (lineItem.channel && lineItem.channel.toLowerCase().includes(searchLower)) ||
          formatNumber(lineItem.qty).includes(searchLower) ||
          formatCurrency(lineItem.advertiser_spend).toLowerCase().includes(searchLower) ||
          formatCurrency(lineItem.amount_to_bill || 0).toLowerCase().includes(searchLower) ||
          (lineItem.invoice_line_item_status || '').toLowerCase().includes(searchLower) ||
          (lineItem.platform && lineItem.platform.toLowerCase().includes(searchLower)) ||
          (lineItem.sku && getSkuNameById(lineItem.sku).toLowerCase().includes(searchLower)) ||
          (lineItem.net_spend !== null && formatCurrency(lineItem.net_spend).toLowerCase().includes(searchLower)) ||
          (lineItem.margin !== null && formatCurrency(lineItem.margin).toLowerCase().includes(searchLower)) ||
          (lineItem.margin_pct !== null && formatPercentage(lineItem.margin_pct).includes(searchLower)) ||
          (lineItem.notes && lineItem.notes.toLowerCase().includes(searchLower)) ||
          (lineItem.do_not_bill && 'do not bill'.includes(searchLower)) ||
          (!lineItem.do_not_bill && 'bill'.includes(searchLower))
        );
      });

      return invoiceMatches || lineItemMatches;
    });
  }, [sortedInvoices, searchValue, companyLookup, termsLookup, skusLookup]);

  const getTextFieldValue = (fieldId: string, defaultValue: string): string => {
    return textFieldValues[fieldId] !== undefined ? textFieldValues[fieldId] : defaultValue;
  };

  const updateTextFieldValue = (fieldId: string, value: string) => {
    setTextFieldValues(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const clearLineItemTextFields = (lineItemId: string) => {
    setTextFieldValues(prev => {
      const newValues = { ...prev };
      // Clear all possible text field keys for this line item
      Object.keys(newValues).forEach(key => {
        if (key.endsWith(`_${lineItemId}`)) {
          delete newValues[key];
        }
      });
      return newValues;
    });
  };

  const cleanNumericInput = (value: string): string => {
    return value.replace(/[^\d.-]/g, '');
  };

  const calculateInterconnectedValues = (field: string, value: number, lineItem: any) => {
    const updates: Record<string, any> = {};

    switch (field) {
      case 'net_spend': {
        // If net spend changes, calculate new margin
        const newMargin = value * lineItem.margin_pct;
        updates.margin = newMargin;
        break;
      }

      case 'margin': {
        // If margin changes, calculate new margin percentage
        const newMarginPctFromMargin = lineItem.net_spend > 0 ? value / lineItem.net_spend : 0;
        updates.margin_pct = newMarginPctFromMargin;
        break;
      }

      case 'margin_pct': {
        // If margin percentage changes, calculate new margin
        const newMarginFromPct = lineItem.net_spend * value;
        updates.margin = newMarginFromPct;
        break;
      }
    }

    return updates;
  };

  const getStatusColor = (status: string, isDot: boolean = false) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case 'approved':
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      case 'synced':
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const renderStatusBadge = (status: string, attention_needed: boolean = false) => {
    if (attention_needed) {
      return (
        <span className="inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm text-[#FF0000]">
          <div className="size-1 rounded-full mr-1 bg-[#FF0000]" />
          Review
        </span>
      );
    }

    return (
      <span
        className={`inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm ${getStatusColor(status)}`}
      >
        <div className={`size-1 rounded-full mr-1 ${getStatusColor(status, true)}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const isItemEditable = (invoice: Invoice, lineItem?: any): boolean => {
    // If approved or synced, not editable
    if (invoice.invoice_status?.toLowerCase() === 'synced' || invoice.invoice_status?.toLowerCase() === 'approved') {
      return false;
    }
    if (lineItem && (lineItem.invoice_line_item_status?.toLowerCase() === 'synced' || lineItem.invoice_line_item_status?.toLowerCase() === 'approved')) {
      return false;
    }
    return true;
  };

  const renderEmptyState = () => {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-white border border-gray-200 rounded-xl py-4 sm:py-32">
        <img
          src={newDashEmptyGraphic}
          className="w-40 h-20"
          alt="Empty state"
        />
        <div className="flex flex-col items-center mt-10 mb-6">
          <div className="text-3xl font-light">
            No invoice groups found.
          </div>
          <div>
            Create your first invoice group to get started.
          </div>
        </div>
      </div>
    );
  };

  const formatMonthYear = (month: number, year: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  const toggleInvoiceExpansion = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  const toggleLineItemExpansion = (lineItemId: string) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(lineItemId)) {
      newExpanded.delete(lineItemId);
    } else {
      newExpanded.add(lineItemId);
    }
    setExpandedLineItems(newExpanded);
  };

  // Optimistic update functions with cache invalidation
  const updateInvoiceField = async(invoiceId: string, field: string, value: any) => {
    try {
      const invoice = filteredInvoices.find(inv => inv.id === invoiceId);

      if (!invoice) {
        console.error('Invoice not found:', invoiceId);
        return;
      }

      // Optimistically update the local state immediately
      if (optimisticInvoiceGroupDetails) {
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(inv =>
          inv.id === invoiceId ? { ...inv, [field]: value } : inv
        );
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }

      if (field === 'invoice_status') {
        await updateInvoiceStatus({
          'id': invoiceId,
          'data': {
            'invoice_status': value,
          },
        }).unwrap();
        setTimeout(refreshInvoices, 10000);
      } else {
        await updateInvoice({
          id: invoiceId,
          data: {
            [field]: value,
            company: field === 'company' ? value : invoice.company,
            invoice_group: selectedGroupId,
          },
        }).unwrap();
      }

      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to update invoice:', error);
      // Revert optimistic update on error
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
    }
  };

  const refreshInvoices = async() => {
    try {
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }

    } catch (error) {
      console.error('Failed to refresh invoices:', error);
    }
  };

  const syncInvoice = async(invoiceId: string) => {
    try {
      await useSyncInvoice({
        id: invoiceId,
      }).unwrap();


      if (optimisticInvoiceGroupDetails) {
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(inv =>
          inv.id === invoiceId ? { ...inv, ['invoice_status']: 'synced' } : inv
        );
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }

      setTimeout(refreshInvoices, 10000);

    } catch (error) {
      console.error('Failed to sync invoice:', error);
    }
  };

  const updateLineItemField = async(lineItemId: string, field: string, value: any) => {
    try {
      // Convert display values to backend values
      let backendValue = value;
      if (field === 'margin_pct' && value !== '') {
        backendValue = parseFloat(value) / 100;
      }

      const lineItem = filteredInvoices
        .flatMap(invoice => invoice.invoice_line_items)
        .find(item => item.id === lineItemId);

      if (!lineItem) {
        console.error('Line item not found:', lineItemId);
        return;
      }

      // Calculate interconnected values for financial fields
      const interconnectedUpdates = calculateInterconnectedValues(field, backendValue, lineItem);
      const allUpdates = { [field]: backendValue, ...interconnectedUpdates };

      // Optimistically update the local state immediately
      if (optimisticInvoiceGroupDetails) {
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(invoice => ({
          ...invoice,
          invoice_line_items: invoice.invoice_line_items.map(item =>
            item.id === lineItemId ? { ...item, ...allUpdates } : item
          ),
        }));
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }

      await updateInvoiceLineItem({
        id: lineItemId,
        data: {
          ...allUpdates,
          invoice_group: lineItem.invoice_group,
          invoice: field === 'invoice' ? value : lineItem.invoice,
        },
      }).unwrap();

      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to update line item:', error);
      // Revert optimistic update, clear all text field values on error
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
      clearLineItemTextFields(lineItemId);
    }
  };

  if (!selectedGroupId) {
    return (
      <div className="flex-grow min-h-0 flex flex-col">
        {renderEmptyState()}
      </div>
    );
  }

  if (isLoadingDetails) {
    return (
      <>
        <div className="flex justify-center items-center py-20">
          <div className="text-gray-500">
            {isGenerating ? 'Generating invoices...' : 'Loading invoice details...'}
          </div>
        </div>
      </>
    );
  }

  const lineItemsNeedAttention = (invoice: Invoice) => {
    return invoice.invoice_line_items.some(item => item.attention_needed);
  };

  const isEmpty = filteredInvoices.length === 0;

  if (isEmpty) {
    if (isGenerating && optimisticInvoiceGroupDetails) {
      return (
        <div className="flex-grow min-h-0 flex flex-col">
          <div className="flex flex-col h-full items-center justify-center bg-white border border-gray-200 rounded-xl py-4 sm:py-32">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-4" />
            <div className="text-3xl font-light">
              Generating invoices...
            </div>
            <div className="text-gray-500 mt-2">
              {`Generating invoices for ${formatMonthYear(optimisticInvoiceGroupDetails.month, optimisticInvoiceGroupDetails.year)}. This may take a moment.`}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-grow min-h-0 flex flex-col">
        <div className="flex flex-col h-full items-center justify-center bg-white border border-gray-200 rounded-xl py-4 sm:py-32">
          <img
            src={newDashEmptyGraphic}
            className="w-40 h-20"
            alt="Empty state"
          />
          <div className="text-3xl font-light mt-10">
            No invoices found.
          </div>
          <div className="mb-6">
            {`No invoices available for ${optimisticInvoiceGroupDetails
              ? formatMonthYear(optimisticInvoiceGroupDetails.month, optimisticInvoiceGroupDetails.year)
              : 'the selected month'}.`}
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => onOpenPreviewModal?.(selectedGroupId)}
              variant="outline"
              className="flex items-center gap-2"
            >
              Preview & Edit Mappings
            </Button>
            <Button
              onClick={() => onGenerateInvoices?.(selectedGroupId)}
              className="bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90 flex items-center gap-2"
            >
              Generate Invoices
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow min-h-0 flex flex-col">
      <div className="w-full bg-white border border-gray-200 p-4 pt-2 flex-grow min-h-0 flex flex-col rounded-xl">
        <div className="flex-grow min-h-0 overflow-auto rounded-lg">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 rounded-t-lg">
              <TableRow className="border-none rounded-t-lg">
                {/* <TableHead className="w-[3%] min-w-[30px] py-1.5" /> */}
                <TableHead className="w-[3%] min-w-[30px] py-1.5 rounded-tl-lg" />
                <TableHead className="text-gray-600 py-1.5 w-[35%] min-w-[250px] max-w-[250px]">
                  Invoice
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 w-[9%] min-w-[70px] max-w-[70px]">
                  Channel
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Impressions
                </TableHead>
                {/* <TableHead className="text-gray-600 py-1.5 text-right w-[11%] min-w-[90px] max-w-[90px]">
                  Ad Spend
                </TableHead> */}
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Amount to Bill
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-center w-[15%] min-w-[100px] max-w-[100px]">
                  Status
                </TableHead>
                {/* <TableHead className="w-[3%] min-w-[30px] py-1.5 rounded-tr-lg" /> */}
              </TableRow>
            </TableHeader>
            <TableBody className='rounded-lg'>
              {filteredInvoices.filter(invoice => !viewHidden ? !invoice.hide : true).map((invoice, invoiceIndex) => {
                const isExpanded = expandedInvoices.has(invoice.id);
                // const isSelected = selectedItems.has(invoice.id);
                const totals = getInvoiceTotals(invoice);
                const isUnassigned = !invoice.company || !companyLookup[invoice.company];
                const isFirstRow = invoiceIndex === 0;
                const isLastRow = invoiceIndex === filteredInvoices.length - 1;

                return (
                  <>
                    {/* Invoice Row */}
                    <TableRow
                      key={invoice.id}
                      className={`transition-colors border-b border-gray-200 cursor-pointer ${
                        isUnassigned
                          ? 'bg-[#FFEBEE]'
                          : invoice.attention_needed
                            ? 'hover:bg-[#FFEBEE]'
                            : 'hover:bg-gray-200'
                      } ${
                        !isUnassigned && (invoiceIndex % 2 === 0 ? 'bg-white' : 'bg-white')
                      } ${
                        isFirstRow ? 'rounded-t-lg' : ''
                      } ${
                        isLastRow && !isExpanded ? 'rounded-b-lg' : ''
                      }`}
                      onClick={() => toggleInvoiceExpansion(invoice.id)}
                    >

                      <TableCell
                        className={`py-1.5 w-[3%] min-w-[30px] cursor-pointer ${
                          isFirstRow ? 'rounded-tl-lg' : ''
                        }`}
                        onClick={() => toggleInvoiceExpansion(invoice.id)}
                      >
                        <div>
                          {isExpanded ? (
                            <ChevronDown className={`h-4 w-4 ${invoice.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
                          ) : (
                            <ChevronRight className={`h-4 w-4 ${invoice.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 w-[33%] min-w-[250px] max-w-[250px]">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <div className="min-w-0 flex-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`flex font-medium truncate ${invoice.attention_needed || lineItemsNeedAttention(invoice) ? 'text-[#FF0000]' : ''}`}>
                                    {invoice.hide && viewHidden && (
                                      <span className="flex items-center mr-2">
                                        <EyeOff className="h-4 w-4 text-red-500" />
                                      </span>
                                    )}
                                    {getCompanyName(invoice.company)}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-none py-2">
                                  <p>
                                    {getCompanyName(invoice.company)}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className={`text-xs truncate ${invoice.attention_needed || lineItemsNeedAttention(invoice) ? 'text-[#CC0000]' : 'text-gray-500'}`}>
                              {invoice.invoice_line_items.length}
                              {' '}
                              line item
                              {invoice.invoice_line_items.length !== 1 ? 's' : ''}
                              {' - '}
                              {formatLastSynced(invoice.last_sync)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`py-1.5 w-[9%] min-w-[70px] max-w-[70px] ${invoice.attention_needed || lineItemsNeedAttention(invoice) ? 'text-[#FF0000]' : ''}`}>
                        {getInvoiceChannelDisplay(invoice)}
                      </TableCell>
                      <TableCell className={`py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px] ${invoice.attention_needed || lineItemsNeedAttention(invoice) ? 'text-[#FF0000]' : ''}`}>
                        {formatNumber(totals.impressions)}
                      </TableCell>
                      {/* <TableCell className={`py-1.5 text-right w-[11%] min-w-[90px] max-w-[90px] ${invoice.attention_needed ? 'text-[#FF0000]' : ''}`}>
                        {formatCurrency(totals.adSpend)}
                      </TableCell> */}
                      <TableCell className={`py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px] ${invoice.attention_needed || lineItemsNeedAttention(invoice) ? 'text-[#FF0000]' : ''}`}>
                        {formatCurrency(totals.amountToBill)}
                      </TableCell>
                      <TableCell className="py-1.5 text-center w-[10%] min-w-[100px] max-w-[100px]">
                        {renderStatusBadge(invoice.invoice_status, invoice.attention_needed || lineItemsNeedAttention(invoice))}
                      </TableCell>
                      <TableCell
                        className={`py-1.5 w-[3%] min-w-[30px] ${
                          isFirstRow ? 'rounded-tr-lg' : ''
                        } ${
                          isLastRow && !isExpanded ? 'rounded-br-lg' : ''
                        }`}
                      >
                        <InvoiceOptionsDropdown
                          invoice={invoice}
                          selectedGroupId={selectedGroupId}
                          updateInvoiceField={updateInvoiceField}
                        />

                      </TableCell>
                    </TableRow>

                    {/* Invoice Details Card (when invoice is expanded) */}
                    {isExpanded && invoice.company && (
                      <TableRow
                        className={`border-b border-gray-100 ${
                          invoiceIndex % 2 === 0 ? 'bg-white' : 'bg-white'
                        }`}
                      >
                        <TableCell
                          colSpan={9}
                          className="py-2 px-4"
                        >
                          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm ml-6">
                            <div className="grid grid-cols-7 gap-3">
                              <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-500">
                                  Bill To
                                </label>
                                <select
                                  value={invoice.company || ''}
                                  onChange={(e) => updateInvoiceField(invoice.id, 'company', e.target.value)}
                                  disabled={!isItemEditable(invoice)}
                                  className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                    !isItemEditable(invoice) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                  }`}
                                >
                                  <option
                                    value=""
                                    disabled
                                  >
                                    Select Company
                                  </option>
                                  {sortedCompanyNames.map((company) => (
                                    <option
                                      key={company.id}
                                      value={company.id}
                                    >
                                      {company.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-500">
                                  Bill To Email
                                </label>
                                <input
                                  type="text"
                                  value={companyLookup[invoice.company]?.emails || '-'}
                                  disabled
                                  className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm h-10 cursor-not-allowed ${
                                    !isItemEditable(invoice) ? 'bg-gray-100 text-gray-500' : 'text-gray-900'
                                  }`}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="text-xs font-medium text-gray-500">
                                  Terms
                                </label>
                                <select
                                  value={invoice.terms || ''}
                                  onChange={(e) => updateInvoiceField(invoice.id, 'terms', e.target.value)}
                                  disabled={!isItemEditable(invoice)}
                                  className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                    !isItemEditable(invoice) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                  }`}
                                >
                                  <option
                                    value=""
                                    disabled
                                  >
                                    Select Terms
                                  </option>
                                  {sortedTerms.map((term) => (
                                    <option
                                      key={term.id}
                                      value={term.id}
                                    >
                                      {term.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-span-1">
                                <label className="text-xs font-medium text-gray-500">
                                  Has Balance
                                </label>
                                <div className="mt-1 h-10 flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={invoice.has_balance}
                                    onChange={(e) => updateInvoiceField(invoice.id, 'has_balance', e.target.checked)}
                                    disabled={!isItemEditable(invoice)}
                                    className={`h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded ${
                                      !isItemEditable(invoice) ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                  />
                                  <span
                                    className={`ml-2 text-sm ${
                                      !isItemEditable(invoice) ? 'text-gray-500' : 'text-gray-700'
                                    }`}
                                  >
                                    Has Balance
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Line Items Rows (when expanded) */}
                    {isExpanded && invoice.invoice_line_items.filter(invoice_line_item => !viewHidden ? !invoice_line_item.hide : true).map((lineItem) => {
                    // const isLineItemSelected = selectedItems.has(lineItem.id);
                      const isLineItemExpanded = expandedLineItems.has(lineItem.id);

                      return (
                        <>
                          <TableRow
                            key={lineItem.id}
                            className={`transition-colors border-b border-gray-100 cursor-pointer w-full ${
                              lineItem.attention_needed
                                ? 'hover:bg-[#FFEBEE]'
                                : 'hover:bg-gray-100'
                            } ${
                              invoiceIndex % 2 === 0 ? 'bg-white' : 'bg-white'
                            }`}
                            onClick={() => toggleLineItemExpansion(lineItem.id)}
                          >
                            <TableCell
                              className="py-1.5 w-[3%] min-w-[30px] cursor-pointer"
                              onClick={() => toggleLineItemExpansion(lineItem.id)}
                            >
                              <div className="relative left-[calc(150%)]">
                                {isLineItemExpanded ? (
                                  <ChevronDown className={`h-4 w-4 ${lineItem.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
                                ) : (
                                  <ChevronRight className={`h-4 w-4 ${lineItem.attention_needed ? 'text-[#FF0000]' : 'text-gray-500'}`} />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5 w-[35%] min-w-[250px] max-w-[250px]">
                              <div className="flex items-center gap-2 overflow-hidden pl-6">
                                <div className="min-w-0 flex-1">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`flex font-medium truncate ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
                                          {lineItem.hide && viewHidden && (
                                            <span className="flex items-center mr-2">
                                              <EyeOff className="h-4 w-4 text-red-500" />
                                            </span>
                                          )}
                                          {lineItem.name}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-none py-2">
                                        <p>
                                          {lineItem.name}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <div className={`text-xs truncate ${lineItem.attention_needed ? 'text-[#CC0000]' : 'text-gray-500'}`}>
                                    {formatLastSynced(lineItem.last_sync)}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-1.5 w-[9%] min-w-[70px] max-w-[70px]">
                              <div
                                className={`truncate capitalize ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}
                                title={lineItem.channel ? lineItem.channel.charAt(0).toUpperCase() + lineItem.channel.slice(1) : 'N/A'}
                              >
                                {lineItem.channel ? lineItem.channel.charAt(0).toUpperCase() + lineItem.channel.slice(1) : 'N/A'}
                              </div>
                            </TableCell>
                            <TableCell className={`py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px] ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
                              {formatNumber(lineItem.qty)}
                            </TableCell>
                            {/* <TableCell className={`py-1.5 text-right w-[11%] min-w-[90px] max-w-[90px] ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
                              {formatCurrency(lineItem.advertiser_spend)}
                            </TableCell> */}
                            <TableCell className={`py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px] ${lineItem.attention_needed ? 'text-[#FF0000]' : ''}`}>
                              {formatCurrency(lineItem.amount_to_bill || 0)}
                            </TableCell>
                            <TableCell className="py-1.5 text-center w-[10%] min-w-[100px] max-w-[100px]">
                              {renderStatusBadge(lineItem.invoice_line_item_status, lineItem.attention_needed)}
                            </TableCell>
                            <TableCell className="py-1.5 w-[3%] min-w-[30px]">

                              <InvoiceLineItemOptionsDropdown
                                companies={companies}
                                invoices={filteredInvoices}
                                lineItem={lineItem}
                                updateLineItemField={updateLineItemField}
                              />

                            </TableCell>
                          </TableRow>

                          {/* Line Item Details (when expanded) */}
                          {isLineItemExpanded && (
                            <TableRow
                              className={`border-b border-gray-100 ${
                                invoiceIndex % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                              }`}
                            >
                              <TableCell
                                colSpan={9}
                                className="py-2 px-4"
                              >
                                <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm ml-16">
                                  <div className="grid grid-cols-4 gap-3">
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Platform
                                      </label>
                                      <select
                                        value={lineItem.platform || ''}
                                        onChange={(e) => updateLineItemField(lineItem.id, 'platform', e.target.value)}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                      >
                                        <option
                                          value=""
                                          disabled
                                        >
                                          Select Platform
                                        </option>
                                        {platforms.map((platform) => (
                                          <option
                                            key={platform}
                                            value={platform}
                                          >
                                            {platform}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Channel
                                      </label>
                                      <select
                                        value={lineItem.channel || ''}
                                        onChange={(e) => updateLineItemField(lineItem.id, 'channel', e.target.value)}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                      >
                                        <option
                                          value=""
                                          disabled
                                        >
                                          Select Channel
                                        </option>
                                        <option value="programmatic">
                                          Programmatic
                                        </option>
                                        <option value="search">
                                          Search
                                        </option>
                                        <option value="social">
                                          Social
                                        </option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        SKU
                                      </label>
                                      <select
                                        value={lineItem.sku || ''}
                                        onChange={(e) => updateLineItemField(lineItem.id, 'sku', e.target.value)}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                      >
                                        <option
                                          value=""
                                          disabled
                                        >
                                          Select SKU
                                        </option>
                                        {sortedSKUs.map((sku) => (
                                          <option
                                            key={sku.id}
                                            value={sku.id}
                                          >
                                            {sku.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Do Not Bill
                                      </label>
                                      <div className="mt-1 h-10 flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={lineItem.do_not_bill}
                                          onChange={(e) => updateLineItemField(lineItem.id, 'do_not_bill', e.target.checked)}
                                          disabled={!isItemEditable(invoice, lineItem)}
                                          className={`h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded ${
                                            !isItemEditable(invoice, lineItem) ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        />
                                        <span
                                          className={`ml-2 text-sm ${
                                            !isItemEditable(invoice, lineItem) ? 'text-gray-500' : 'text-gray-700'
                                          }`}
                                        >
                                          Do Not Bill
                                        </span>
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Impressions
                                      </label>
                                      <input
                                        type="text"
                                        value={getTextFieldValue(`qty_${lineItem.id}`, lineItem.qty !== null && lineItem.qty !== undefined ? lineItem.qty.toString() : '')}
                                        onChange={(e) => updateTextFieldValue(`qty_${lineItem.id}`, cleanNumericInput(e.target.value))}
                                        onBlur={(e) => {
                                          const value = cleanNumericInput(e.target.value);
                                          if (value === '') {
                                            // Reset to original value if empty
                                            updateTextFieldValue(`qty_${lineItem.id}`, lineItem.qty !== null && lineItem.qty !== undefined ? lineItem.qty.toString() : '');
                                          } else {
                                            const intValue = parseInt(value);
                                            if (!isNaN(intValue)) {
                                              updateLineItemField(lineItem.id, 'qty', intValue);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const intValue = parseInt(cleanNumericInput((e.target as HTMLInputElement).value));
                                            if (!isNaN(intValue)) {
                                              updateLineItemField(lineItem.id, 'qty', intValue);
                                            }
                                            (e.target as HTMLInputElement).blur();
                                          }
                                        }}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                        placeholder="0"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Platform Spend ($)
                                      </label>
                                      <input
                                        type="text"
                                        value={getTextFieldValue(`net_spend_${lineItem.id}`, lineItem.net_spend !== null && lineItem.net_spend !== undefined ? Number(lineItem.net_spend).toFixed(2) : '')}
                                        onChange={(e) => updateTextFieldValue(`net_spend_${lineItem.id}`, cleanNumericInput(e.target.value))}
                                        onBlur={(e) => {
                                          const value = cleanNumericInput(e.target.value);
                                          if (value === '') {
                                            // Reset to original value if empty
                                            updateTextFieldValue(`net_spend_${lineItem.id}`, lineItem.net_spend !== null && lineItem.net_spend !== undefined ? Number(lineItem.net_spend).toFixed(2) : '');
                                          } else {
                                            const numValue = parseFloat(value);
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'net_spend', numValue);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const numValue = parseFloat(cleanNumericInput((e.target as HTMLInputElement).value));
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'net_spend', numValue);
                                            }
                                            (e.target as HTMLInputElement).blur();
                                          }
                                        }}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Margin ($)
                                      </label>
                                      <input
                                        type="text"
                                        value={getTextFieldValue(`margin_${lineItem.id}`, lineItem.margin !== null && lineItem.margin !== undefined ? Number(lineItem.margin).toFixed(2) : '')}
                                        onChange={(e) => updateTextFieldValue(`margin_${lineItem.id}`, cleanNumericInput(e.target.value))}
                                        onBlur={(e) => {
                                          const value = cleanNumericInput(e.target.value);
                                          if (value === '') {
                                            // Reset to original value if empty
                                            updateTextFieldValue(`margin_${lineItem.id}`, lineItem.margin !== null && lineItem.margin !== undefined ? Number(lineItem.margin).toFixed(2) : '');
                                          } else {
                                            const numValue = parseFloat(value);
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'margin', numValue);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const numValue = parseFloat(cleanNumericInput((e.target as HTMLInputElement).value));
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'margin', numValue);
                                            }
                                            (e.target as HTMLInputElement).blur();
                                          }
                                        }}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-gray-500">
                                        Margin (%)
                                      </label>
                                      <input
                                        type="text"
                                        value={getTextFieldValue(`margin_pct_${lineItem.id}`, lineItem.margin_pct !== null && lineItem.margin_pct !== undefined ? (lineItem.margin_pct * 100 % 1 === 0 ? Math.round(lineItem.margin_pct * 100).toString() : (lineItem.margin_pct * 100).toFixed(2)) : '')}
                                        onChange={(e) => updateTextFieldValue(`margin_pct_${lineItem.id}`, cleanNumericInput(e.target.value))}
                                        onBlur={(e) => {
                                          const value = cleanNumericInput(e.target.value);
                                          if (value === '') {
                                            // Reset to original value if empty
                                            updateTextFieldValue(`margin_pct_${lineItem.id}`, lineItem.margin_pct !== null && lineItem.margin_pct !== undefined ? (lineItem.margin_pct * 100 % 1 === 0 ? Math.round(lineItem.margin_pct * 100).toString() : (lineItem.margin_pct * 100).toFixed(2)) : '');
                                          } else {
                                            const numValue = parseFloat(value);
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'margin_pct', numValue);
                                            }
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const numValue = parseFloat(cleanNumericInput((e.target as HTMLInputElement).value));
                                            if (!isNaN(numValue)) {
                                              updateLineItemField(lineItem.id, 'margin_pct', numValue);
                                            }
                                            (e.target as HTMLInputElement).blur();
                                          }
                                        }}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <div className="col-span-4">
                                      <label className="text-xs font-medium text-gray-500">
                                        Notes
                                      </label>
                                      <input
                                        type="text"
                                        value={getTextFieldValue(`notes_${lineItem.id}`, lineItem.notes || '')}
                                        onChange={(e) => updateTextFieldValue(`notes_${lineItem.id}`, e.target.value)}
                                        onBlur={(e) => updateLineItemField(lineItem.id, 'notes', e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            updateLineItemField(lineItem.id, 'notes', (e.target as HTMLInputElement).value);
                                            (e.target as HTMLInputElement).blur();
                                          }
                                        }}
                                        disabled={!isItemEditable(invoice, lineItem)}
                                        className={`w-full mt-1 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-10 ${
                                          !isItemEditable(invoice, lineItem) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                                        }`}
                                        placeholder="Enter notes..."
                                      />
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })}

                    {/* Add Line Item Row */}
                    {isExpanded && invoice.invoice_status?.toLowerCase() !== 'approved' && (
                      <TableRow
                        className={`transition-colors border-b border-gray-100 cursor-pointer ${
                          invoiceIndex % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                        } ${
                          isLastRow ? 'rounded-b-lg' : ''
                        }`}
                      >
                        <TableCell
                          className={`py-1.5 w-[3%] min-w-[30px] cursor-pointer ${
                            isLastRow ? 'rounded-bl-lg' : ''
                          }`}
                        >
                          <div className="relative left-[calc(150%)]" />
                        </TableCell>
                        <TableCell className="py-1.5 w-[33%] min-w-[250px] max-w-[250px]">
                          <div className="flex items-center gap-2 overflow-visible pl-6">
                            <div className="min-w-0 flex-1">
                              {showNewLineItemInputs.has(invoice.id) ? (
                                <div className="flex items-center gap-2 w-full">
                                  <Input
                                    type="text"
                                    placeholder="Line item name"
                                    value={newLineItemInputs[invoice.id] || ''}
                                    onChange={(e) => handleNewLineItemInputChange(invoice.id, e.target.value)}
                                    onKeyDown={(e) => handleNewLineItemKeyDown(invoice.id, e)}
                                    className="flex-1 min-w-[200px] rounded-xl bg-white border-none shadow-none"
                                    disabled={!isItemEditable(invoice)}
                                    autoFocus
                                  />
                                  <Button
                                    onClick={() => {
                                      const name = newLineItemInputs[invoice.id]?.trim();
                                      if (name) {
                                        handleCreateNewLineItem(invoice.id, name);
                                      }
                                    }}
                                    disabled={!isItemEditable(invoice) || !newLineItemInputs[invoice.id]?.trim()}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-2 h-8 w-8 rounded-full"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => toggleNewLineItemInput(invoice.id)}
                                    disabled={!isItemEditable(invoice)}
                                    className="flex-shrink-0 rounded-full px-2 py-2 h-8 w-8"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div
                                  className="font-medium text-gray-500 hover:text-gray-700 cursor-pointer h-8 flex items-center"
                                  // onClick={() => toggleNewLineItemInput(invoice.id)}
                                >
                                  <Button
                                    variant="outline"
                                    onClick={() => toggleNewLineItemInput(invoice.id)}
                                    className="h-8 px-3 flex-shrink-0 rounded-xl hover:bg-gray-500 hover:text-white"
                                  >
                                    <Plus className="h-4 w-4" />
                                    Add new line item
                                  </Button>

                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 w-[9%] min-w-[70px] max-w-[70px]" />
                        <TableCell className="py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px]" />
                        {/* <TableCell className="py-1.5 text-right w-[11%] min-w-[90px] max-w-[90px]" /> */}
                        <TableCell className="py-1.5 text-right w-[12%] min-w-[100px] max-w-[100px]" />
                        <TableCell className="py-1.5 text-center w-[10%] min-w-[100px] max-w-[100px]" />
                        <TableCell
                          className={`py-1.5 w-[3%] min-w-[30px] ${
                            isLastRow ? 'rounded-br-lg' : ''
                          }`}
                        />
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

InvoiceTable.displayName = 'InvoiceTable';

export default InvoiceTable;